(ns minorodata.components.connections.extract-details-drawer
  (:require
   [clojure.string :as str]
   ["copy-to-clipboard" :as copy]
   [lucide-react :refer [Check CircleX Copy CopyPlus DatabaseIcon Info PlayCircle RefreshCw Trash2 X]]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.app.utils :as app.utils]
   [minorodata.components.connections.extract-edit-form :as extract-edit-form]
   [minorodata.components.connections.extract-logs :as extract-logs]
   [minorodata.components.connections.extract-notification-form :as extract-notification-form]
   [minorodata.components.connections.extract-output-preview :as extract-output-preview]
   [minorodata.components.connections.extract-pipeline-preview :as extract-pipeline-preview]
   [minorodata.components.connections.extract-table-details :as extract-table-details]
   [minorodata.components.forms.doc :as docs]
   [minorodata.components.schedule.core :as c.schedule]
   [mold-forms.core :as form-component]
   [mold-forms.model :refer [->form-model]]
   [react :as react]
   [re-posh.core :as re-posh]
   [ui.dialog :refer [dialog-actions dialog-content dialog-root dialog-title]]
   [ui.date-time :refer [date-tooltip]]
   [ui.button :refer [button]]
   [ui.tabs :as tabs]
   [ui.tooltip :refer [tooltip]]
   [ui.typography :as typography]
   [ui.utils :refer [clsx]]))

(def job-status-mapping
  {"pending" :starting
   "fail"    :failed
   "success" :success})

(def tab-items
  [{:key "details"       :label "Details"}
   {:key "edit"          :label "Edit"}
   {:key "schedule"      :label "Schedule"}
   {:key "notifications" :label "Notifications"}
   {:key "logs"          :label "Logs"}
   {:key "table-details" :label "Table details"}])

(defn extract-output-table [extract]
  (get-in extract [:extract/config :extracts 0 :destination-table]))

(defn extract-table-details-params [extract]
  (let [[table schema catalog]
        (some-> (extract-output-table extract)
                (str/split #"\.")
                reverse)]
    (when (and catalog schema table)
      {:catalog catalog
       :schema  schema
       :table   table})))

(defn schedule-extract-submit [extract on-success form-data]
  (let [{:extract/keys [extract-id source-id master-id]} extract
        {:keys [cron]} (form-component/model->fields form-data)]
    (dispatch
     [:connections.events/bulk-schedule-extracts
      {:extract-ids             [extract-id]
       :source-id               source-id
       :master-id               master-id
       :active-extract-id       extract-id
       :extract-details-drawer? true
       :data                    {:schedule-spec {:cron cron}}
       :on-success              on-success}])))

(defn unschedule-extract [extract on-success]
  (dispatch [:connections.events/bulk-unschedule-extracts
             {:extract-ids [(:extract/extract-id extract)]
              :source-id   (:extract/source-id extract)
              :on-success  on-success}]))

(defn unschedule-dialog [{:keys [extract]}]
  (let [[open? set-open] (react/useState false)]
    [:<>
     [button {:type     "button"
              :variant  :secondary
              :data-cy  "extract-unschedule-btn"
              :on-click #(set-open true)}
      "Unschedule"]
     [dialog-root {:open           open?
                   :on-open-change set-open}
      [dialog-content {:size :small}
       [dialog-title "Unschedule extract"]
       [:p {:class "mt-4"}
        "This action will unschedule this extract" [:br]
        [:strong "Are you sure you want to proceed?"]]
       [dialog-actions
        [button {:variant  :secondary
                 :on-click #(set-open false)}
         "Cancel"]
        [button {:on-click (fn []
                             (set-open false)
                             (unschedule-extract
                              extract
                              (fn []
                                (dispatch [:connections.api/fetch-extract
                                           {:extract-id (:extract/extract-id extract)
                                            :source-id  (:extract/source-id extract)}]))))}
         "Sure"]]]]]))

(defn delete-dialog [{:keys [extract-name on-delete]}]
  (let [[open? set-open] (react/useState false)]
    [:<>
     [tooltip {:content          "Delete extract"
               :trigger-as-child false}
      [button {:size     :icon-md
               :variant  :secondary
               :icon     Trash2
               :class    "text-kleene-error-01"
               :on-click #(set-open true)}]]
     [dialog-root {:open           open?
                   :on-open-change set-open}
      [dialog-content {:size :small}
       [dialog-title "Delete extract"]
       [:p {:class "mt-4"}
        "Do you really wish to delete extract "
        [:strong "\"" extract-name "\""]
        "?"]
       [dialog-actions
        [button {:variant  :secondary
                 :on-click #(set-open false)}
         "Cancel"]
        [button {:class    "bg-kleene-error-01 text-white hover:bg-kleene-error-01/90"
                 :on-click (fn []
                             (set-open false)
                             (on-delete))}
         "Delete extract"]]]]]))

(defn resolve-last-run
  [{:keys [message-queue-enabled]} {:extract/keys [extract-id last-run-status last-run-at]}]
  (if message-queue-enabled
    (let [task-status @(re-posh/subscribe [:extract-task-status/pull [:extract-task-status/extract-id extract-id]])]
      {:status (:extract-task-status/status task-status)
       :run-at (:extract-task-status/created-at task-status)})
    {:status last-run-status
     :run-at last-run-at}))

(defn run-status-label [status]
  (case (get job-status-mapping status status)
    :failed   "Failed"
    :success  "Finished"
    :starting "Starting"
    status))

(defn details-tab [{:keys [extract source source-definition output-table timezone]}]
  (let [{:extract/keys [description downstream-config]} extract
        {:keys [status run-at]} (resolve-last-run source-definition extract)
        run-downstream?         (= "full" downstream-config)]
    [:div {:class "w-full space-y-6"}
     [:section
      [typography/h3 "Description"]
      [typography/p {:class "mt-2 text-kleene-text-secondary"}
       (if (str/blank? description)
         "No description"
         description)]]

     [:section
      [typography/h3
       "Schedule "
       [:span {:class "font-normal text-kleene-text-secondary"}
        (str "(" timezone " time zone)")]]
      [typography/p {:class "mt-2 text-kleene-text-secondary"}
       (app.utils/extract-schedule extract)]]

     [:section
      [typography/h3 "Last run"]
      (if (or status run-at)
        [:div {:class "mt-2 flex items-center gap-2 text-kleene-text-secondary"}
         (when (= "fail" status)
           [:> CircleX {:size 20 :class "text-kleene-error-01"}])
         [:span (run-status-label status)]
         (when run-at
           [date-tooltip {:date run-at}])]

        [typography/p {:class "mt-2 text-kleene-text-secondary"}
         "Never executed"])]

     [:section
      [typography/h3 "Output table"]
      [:div {:class "mt-2 flex items-center gap-4"}
       [:span {:class "break-all"} output-table]

       (when output-table
         [tooltip {:content          "Copy output table"
                   :trigger-as-child false}
          [:> Copy
           {:size     24
            :class    "shrink-0 cursor-pointer hover:text-kleene-text-secondary transition-colors"
            :on-click (fn [e]
                        (.stopPropagation e)
                        (copy output-table)
                        (dispatch [:app.events/add-snack
                                   :success
                                   "Output table copied"]))}]])]]

     (when (extract-edit-form/has-downstream-config?
            (extract-edit-form/extract-form-definition extract source))
       [:section
        [:div {:class "flex items-center"}
         [typography/h3 "Run downstream"]
         [docs/tooltip {:doc extract-edit-form/downstream-config-doc :class "mt-0"}]]
        [:div {:class "mt-2 flex items-center gap-2 text-kleene-text-secondary"}
         (when run-downstream?
           [:> Check {:size 20 :class "text-kleene-text-success"}])
         [:span (if run-downstream? "Enabled" "Disabled")]]])

     [:f> extract-pipeline-preview/pipeline-preview
      {:extract      extract
       :source       source
       :output-table output-table}]

     [:f> extract-output-preview/output-preview
      {:output-table output-table}]]))

(defn edit-tab [{:keys [extract source on-tab-change]}]
  [:div {:class "max-w-[860px]"}
   [extract-edit-form/extract-edit-form
    {:extract                 extract
     :source                  source
     :on-close                #(on-tab-change "details")
     :extract-details-drawer? true}]])

(defn schedule-tab [{:keys [extract timezone]}]
  (let [schedule-label (app.utils/format-schedule
                        {:cron            (app.utils/->cron extract)
                         :empty-status    "Not scheduled"
                         :required-fields [:minute]})
        scheduled?     (not= "Not scheduled" schedule-label)]
    [:div
     [:div {:class "mb-4 flex items-start justify-between gap-4"}
      [:div
       [typography/h4 "Current schedule (" timezone " time zone)"]
       [typography/h4 {:class "mt-2 text-kleene-primary-v2"}
        schedule-label]]

      (when scheduled?
        [:f> unschedule-dialog {:extract extract}])]

     [:hr {:class "my-6"}]

     [form-component/form
      (->form-model
       {:id        :extract-details-scheduler
        :on-submit (partial schedule-extract-submit extract
                            #(dispatch [:app.events/add-snack :success "Schedule saved"]))
        :clean-up  #(dispatch [:form-model/delete [:form-model/id :extract-details-scheduler]])
        :validate  (partial form-component/validate-form ::c.schedule/schedule-form)
        :fields    [{:name    :cron
                     :value   (app.utils/->cron extract)
                     :process identity}]})
      (fn [fields errors on-submit]
        (let [any-error (->> errors vals (map :value) first)]
          [:form {:on-submit on-submit}
           [c.schedule/cron-editor {:field (:cron fields)}]
           (when (some? any-error)
             [:div {:class "mt-4"}
              [:span {:class "text-kleene-error-01"} any-error]])
           [:div {:class "mt-4 flex justify-end"}
            [button {:type    "submit"
                     :data-cy "extract-schedule-submit-btn"}
             "Submit schedule"]]]))]]))

(defn notifications-tab [{:keys [extract]}]
  (let [{:extract/keys [master-id]} extract
        notification     @(re-posh/subscribe [:notifications.subs/job-notification master-id])
        account-settings @(re-posh/subscribe [:account-notifications/pull])]
    [:div {:class "max-w-[560px] space-y-4"}
     [:div {:class "mb-4 flex items-center gap-2"}
      [typography/h3 "Notify when extract execution state changes"]
      [tooltip {:content "You will receive a notification when the latest extract execution status is different from the previous one."}
       [:> Info {:size 20}]]]

     [:f> extract-notification-form/extract-notification-form
      {:master-id        master-id
       :notification     notification
       :account-settings account-settings
       :on-success       [:app.events/add-snack :success "Notification settings saved"]}]]))

(defn logs-tab [{:keys [extract source]}]
  [:div {:class "h-full min-h-0"}
   [:f> extract-logs/extract-logs
    {:extract extract
     :source  source}]])

(defn table-details-tab [{:keys [extract on-close]}]
  (if-let [params (extract-table-details-params extract)]
    [:f> extract-table-details/table-details {:params params :on-close on-close}]
    [:div {:class "space-y-4"}
     [typography/h4 "Table details"]
     [typography/p {:class "text-kleene-text-secondary"}
      "No output table"]]))

(defn extract-details-drawer
  [{:keys [extract source source-definition active-tab on-tab-change on-close on-run on-clone on-delete]}]
  (let [{:extract/keys [name]} extract
        output-table  (extract-output-table extract)
        timezone      @(re-posh/subscribe [:app.subs/timezone])
        extract-id    (:extract/extract-id extract)
        logs-loading? @(re-posh/subscribe [:mold-http/loading? :logs.api/fetch])]

    [:div {:class "flex h-full flex-col overflow-hidden"}
     [:div
      [:div {:class "flex items-start justify-between gap-4 px-5 pt-5 pb-3"}
       [:div {:class "flex items-center gap-4 min-w-0"}
        [:div {:class "flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-extracts/20 text-extracts"}
         [:> DatabaseIcon {:size 28}]]

        [:div {:class "min-w-0"}
         [typography/h2 {:class "truncate"} name]
         [typography/p {:class "text-kleene-text-secondary truncate"}
          (:source/name source)]]]

       [:> X {:class    "shrink-0 cursor-pointer"
              :size     20
              :on-click on-close}]]

      [:div {:class "flex items-end justify-between gap-4 border-b px-5"}
       [tabs/tabs-root
        {:value           active-tab
         :on-value-change on-tab-change}
        [tabs/tabs-list {:variant :underlined
                         :class   "border-b-0 px-0 -mb-px flex-1 min-w-0 justify-between gap-1"}
         (for [{:keys [key label]} tab-items]
           ^{:key key}
           [tabs/tabs-trigger {:value   key
                               :variant :underlined
                               :class   "whitespace-nowrap shrink text-sm px-2"}
            label])]]

       [:div {:class "flex shrink-0 items-center gap-2 pb-1.5"}
        (when (= active-tab "logs")
          [tooltip {:content          "Refresh logs"
                    :trigger-as-child false}
           [button {:size     :icon-md
                    :variant  :secondary
                    :icon     RefreshCw
                    :data-cy  "extract-drawer-refresh-logs-btn"
                    :disabled logs-loading?
                    :on-click #(dispatch [:logs.api/fetch])}]])

        [tooltip {:content          "Run extract"
                  :trigger-as-child false}
         [button {:size     :icon-md
                  :variant  :secondary
                  :icon     PlayCircle
                  :data-cy  "extract-drawer-run-btn"
                  :on-click on-run}]]

        [tooltip {:content          "Clone extract"
                  :trigger-as-child false}
         [button {:size     :icon-md
                  :variant  :secondary
                  :icon     CopyPlus
                  :on-click on-clone}]]

        [:f> delete-dialog {:extract-name name :on-delete on-delete}]]]]

     [:div {:class (clsx "min-h-0"
                         (if (= active-tab "logs")
                           "flex-1 overflow-hidden p-0"
                           "overflow-y-auto px-5 py-4 pb-kai"))}
      (some-> (case active-tab
                "details"
                [:f> details-tab
                 {:extract           extract
                  :source            source
                  :source-definition source-definition
                  :output-table      output-table
                  :timezone          timezone}]

                "edit"
                [edit-tab
                 {:extract       extract
                  :source        source
                  :on-tab-change on-tab-change}]

                "schedule"
                [schedule-tab
                 {:extract  extract
                  :timezone timezone}]

                "notifications"
                [notifications-tab {:extract extract}]

                "logs"
                [logs-tab {:extract extract :source source}]

                "table-details"
                [:f> table-details-tab {:extract extract :on-close on-close}]

                nil)
              (with-meta {:key extract-id}))]]))
